import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlignLeft, Bell, Briefcase, CalendarDays, ChevronLeft, ChevronRight, Clock, Globe, MapPin, Palette,
  Plus, Repeat, Search, Users, Video, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { enlaceMeet } from '../../lib/meetEnlace';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { api } from '../../lib/api';
import { useStore } from '../../store/useStore';
import { initials } from '../../lib/format';
import {
  BYDAY, COLORES, DIAS_CORTOS, UNIDADES, capital, construirRrule, deHhmm, deLocal, describirRrule, diaSemana,
  diffDias, hhmm, hora12, isoUtc, leerRrule, listaZonas, minutosDe, nombreZona, partirRecordatorio, presetDe,
  presetsRepeticion, sumarDias, textoDia, textoDiaLargo, ymdDe,
} from '../../lib/calendario';
import CampoPersonas from './CampoPersonas';
import SelectorHora from './SelectorHora';

/* ------------------------------------------------------------------ forma */

/** Evento (o datos de creación) → estado del formulario, con las horas en la zona del evento. */
export function eventoAForm(ev, { tz, calendarioId, recordatoriosDefecto = [10] } = {}) {
  const zona = ev?.zona || tz;
  if (!ev || !ev.id) {
    // Creación: ev = {ymd, m0, m1, todoElDia, titulo, calendario_id}
    const ymd = ev?.ymd;
    const m0 = ev?.m0 ?? 9 * 60;
    const m1 = ev?.m1 ?? m0 + 60;
    return {
      titulo: ev?.titulo || '', todo_el_dia: !!ev?.todoElDia,
      fIni: ymd, hIni: hhmm(m0), fFin: m1 >= 1440 ? sumarDias(ymd, 1) : ymd, hFin: hhmm(m1 % 1440),
      zona, repModo: '', custom: { ...leerRrule(null), byday: [BYDAY[diaSemana(ymd)]] },
      ubicacion: '', descripcion: '', invitados: [], recordatorios: [...recordatoriosDefecto],
      calendario_id: ev?.calendario_id || calendarioId, color: null, disponibilidad: ev?.todoElDia ? 'libre' : 'ocupado', visibilidad: 'normal',
    };
  }
  const todo = !!ev.todo_el_dia;
  const fIni = todo ? ev.fecha_inicio : ymdDe(ev.inicio, zona);
  const fFin = todo ? sumarDias(ev.fecha_fin, -1) : ymdDe(ev.fin, zona);
  const modo = presetDe(ev.rrule, fIni);
  const custom = ev.rrule ? leerRrule(ev.rrule) : { ...leerRrule(null), byday: [BYDAY[diaSemana(fIni)]] };
  if (custom.freq === 'WEEKLY' && !custom.byday.length) custom.byday = [BYDAY[diaSemana(fIni)]];
  return {
    titulo: ev.titulo || '', todo_el_dia: todo,
    fIni, hIni: todo ? '09:00' : hhmm(minutosDe(ev.inicio, zona)),
    fFin, hFin: todo ? '10:00' : hhmm(minutosDe(ev.fin, zona)),
    zona, repModo: modo === null ? 'custom' : modo, custom,
    ubicacion: ev.ubicacion || '', descripcion: ev.descripcion || '',
    invitados: (ev.invitados || []).map((i) => ({ ...i })),
    recordatorios: [...(ev.recordatorios || [])], calendario_id: ev.calendario_id,
    color: ev.color || null, disponibilidad: ev.disponibilidad || 'ocupado', visibilidad: ev.visibilidad || 'normal',
  };
}

export function rruleDeForm(f) {
  if (!f.repModo) return null;
  if (f.repModo === 'custom') return construirRrule(f.custom);
  const p = presetsRepeticion(f.fIni).find((x) => x.id === f.repModo);
  return p ? p.rrule : null;
}

/** Formulario → cuerpo para POST/PATCH /calendar/events (completo; el que edita compara con el original). */
export function formAPayload(f) {
  const base = {
    titulo: f.titulo.trim(), descripcion: f.descripcion, ubicacion: f.ubicacion.trim(), zona: f.zona,
    rrule: rruleDeForm(f), color: f.color, disponibilidad: f.disponibilidad, visibilidad: f.visibilidad,
    recordatorios: [...new Set(f.recordatorios.filter((m) => Number.isFinite(m) && m >= 0))].sort((a, b) => a - b),
    invitados: f.invitados.map((i) => ({ correo: i.correo, nombre: i.nombre || '', opcional: !!i.opcional })),
    calendario_id: f.calendario_id,
  };
  if (f.todo_el_dia) {
    return { ...base, todo_el_dia: true, fecha_inicio: f.fIni, fecha_fin: sumarDias(f.fFin < f.fIni ? f.fIni : f.fFin, 1) };
  }
  return {
    ...base, todo_el_dia: false,
    inicio: isoUtc(deLocal(f.fIni, deHhmm(f.hIni), f.zona)),
    fin: isoUtc(deLocal(f.fFin, deHhmm(f.hFin), f.zona)),
  };
}

/** Solo lo que cambió respecto al evento original (el PATCH acepta campos sueltos). */
export function diferencias(payload, original) {
  const orig = formAPayload(eventoAForm(original, { tz: original.zona }));
  const salida = {};
  const iguales = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  for (const k of Object.keys(payload)) {
    if (k === 'invitados') {
      const norm = (l) => (l || []).map((i) => `${i.correo}|${i.opcional ? 1 : 0}`).sort();
      if (!iguales(norm(payload.invitados), norm(orig.invitados))) salida.invitados = payload.invitados;
    } else if (!iguales(payload[k], orig[k])) {
      salida[k] = payload[k];
    }
  }
  // La hora va junta: si cambia algo del cuándo, se manda el bloque entero
  const tiempo = ['todo_el_dia', 'inicio', 'fin', 'fecha_inicio', 'fecha_fin'];
  if (tiempo.some((k) => k in salida)) for (const k of tiempo) if (k in payload) salida[k] = payload[k];
  return salida;
}

/* ------------------------------------------------------------------ editor */

const CAMPOS_PARCIALES = new Set(['recordatorios', 'color', 'disponibilidad', 'visibilidad']);

export default function EditorEvento({
  abierto, original, inicial, tz, calendarios, onCerrar, onGuardar, anadirMeet = false,
}) {
  const [f, setF] = useState(inicial);
  const [verAyudaDisp, setVerAyudaDisp] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [buscar, setBuscar] = useState(false);
  const user = useStore((s) => s.user);
  useEffect(() => { if (abierto) { setF(inicial); setError(''); setBuscar(false); } }, [abierto, inicial]);

  const parcial = !!original?.editable_parcial && !original?.editable;
  const fijo = (campo) => parcial && !CAMPOS_PARCIALES.has(campo);
  const set = (cambios) => setF((x) => ({ ...x, ...cambios }));

  // Orvian Meet: crea la sala y pone su enlace en la ubicación (?nuevo=1&meet=1 lo hace solo al abrir)
  const [creandoMeet, setCreandoMeet] = useState(false);
  const meet = enlaceMeet(f?.ubicacion, f?.descripcion);
  const anadirVideollamada = async () => {
    setCreandoMeet(true);
    try {
      const { data } = await api.post('/meet/salas', { titulo: (f.titulo || '').trim() || 'Reunión' });
      setF((x) => ({ ...x, ubicacion: data.url }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la videollamada');
    } finally {
      setCreandoMeet(false);
    }
  };
  const meetAuto = useRef(false);
  useEffect(() => {
    if (abierto && anadirMeet && !meetAuto.current && !enlaceMeet(inicial?.ubicacion)) {
      meetAuto.current = true;
      anadirVideollamada();
    }
  }, [abierto, anadirMeet]); // eslint-disable-line react-hooks/exhaustive-deps
  const zonas = useMemo(() => listaZonas(), []);
  const presets = useMemo(() => (f?.fIni ? presetsRepeticion(f.fIni) : []), [f?.fIni]);

  if (!abierto || !f) return null;

  const cambiarInicio = (fIni, hIni = f.hIni) => {
    // Mantener la duración al mover el inicio, como Google
    const durDias = diffDias(f.fIni, f.fFin);
    const durMin = deHhmm(f.hFin) - deHhmm(f.hIni);
    const cambios = { fIni, hIni, fFin: sumarDias(fIni, durDias) };
    if (!f.todo_el_dia) {
      let fin = deHhmm(hIni) + durMin;
      let fFin = sumarDias(fIni, durDias);
      while (fin >= 1440) { fin -= 1440; fFin = sumarDias(fFin, 1); }
      while (fin < 0) { fin += 1440; fFin = sumarDias(fFin, -1); }
      cambios.hFin = hhmm(fin);
      cambios.fFin = fFin;
    }
    // Repetición semanal de un solo día: sigue al día nuevo
    if (f.repModo === 'custom' && f.custom.freq === 'WEEKLY' && f.custom.byday.length === 1) {
      cambios.custom = { ...f.custom, byday: [BYDAY[diaSemana(fIni)]] };
    }
    set(cambios);
  };

  const guardar = async () => {
    setError('');
    const p = formAPayload(f);
    if (!p.todo_el_dia && new Date(p.fin) <= new Date(p.inicio)) { setError('El final debe ser posterior al inicio.'); return; }
    if (f.repModo === 'custom' && f.custom.freq === 'WEEKLY' && !f.custom.byday.length) { setError('Elige al menos un día de la semana para repetir.'); return; }
    setGuardando(true);
    try {
      const ok = await onGuardar(p, f);
      if (ok === false) return;
    } catch (e) {
      setError(e?.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const invitadoCorreos = f.invitados.map((i) => i.correo);
  const durMin = f.todo_el_dia ? 60 : Math.max(15, (deLocal(f.fFin, deHhmm(f.hFin), f.zona) - deLocal(f.fIni, deHhmm(f.hIni), f.zona)) / 60000);

  return (
    <Dialog open={abierto} onOpenChange={(v) => { if (!v) onCerrar(); }}>
      <DialogContent className="cal-editor p-0 gap-0 [&>button.absolute]:hidden" data-testid="cal-editor">
        <DialogTitle className="sr-only">{original ? 'Editar evento' : 'Nuevo evento'}</DialogTitle>
        <header className="cal-editor-cab">
          <button type="button" className="cal-icono-btn" onClick={onCerrar} aria-label="Cerrar"><X className="h-5 w-5" /></button>
          <input
            className="cal-titulo-input flex-1"
            value={f.titulo}
            onChange={(e) => set({ titulo: e.target.value })}
            placeholder="Añade un título"
            aria-label="Título"
            disabled={fijo('titulo')}
            autoFocus={!original}
            data-testid="cal-editor-titulo"
          />
          <button type="button" className="od-btn-primary" onClick={guardar} disabled={guardando} data-testid="cal-editor-guardar">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </header>

        <div className="cal-editor-cuerpo od-scroll">
          {error && <div className="cal-error" role="alert">{error}</div>}
          {parcial && (
            <div className="cal-aviso">Es una invitación recibida por correo: solo puedes cambiar tus recordatorios, el color, si te marca como ocupado y la privacidad.</div>
          )}
          <div className="cal-editor-columnas">
            <div className="cal-editor-col">
              {/* Cuándo */}
              <div className="cal-campo">
                <Clock className="cal-campo-ico" />
                <div className="flex-1 flex flex-col gap-2">
                  <div className="cal-fechas">
                    <input type="date" className="cal-input" value={f.fIni} onChange={(e) => e.target.value && cambiarInicio(e.target.value)} disabled={fijo('inicio')} aria-label="Fecha de inicio" data-testid="cal-editor-fecha-inicio" />
                    {!f.todo_el_dia && (
                      <SelectorHora value={f.hIni} onChange={(v) => cambiarInicio(f.fIni, v)} disabled={fijo('inicio')} ariaLabel="Hora de inicio" testid="cal-editor-hora-inicio" />
                    )}
                    <span className="cal-fechas-a">a</span>
                    {!f.todo_el_dia && (
                      <SelectorHora value={f.hFin} onChange={(v) => set({ hFin: v })} disabled={fijo('fin')} ariaLabel="Hora de fin" testid="cal-editor-hora-fin" />
                    )}
                    <input type="date" className="cal-input" value={f.fFin} min={f.fIni} onChange={(e) => e.target.value && set({ fFin: e.target.value })} disabled={fijo('fin')} aria-label="Fecha de fin" />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <label className="cal-check">
                      <input type="checkbox" checked={f.todo_el_dia} onChange={(e) => set({
                        todo_el_dia: e.target.checked,
                        disponibilidad: e.target.checked && !original ? 'libre' : f.disponibilidad,
                        // Evento nuevo de día completo: fuera el «10 min antes», que avisaba a las 23:50 de la víspera (K7)
                        ...(e.target.checked && !original && f.recordatorios.length === 1 && f.recordatorios[0] === 10 ? { recordatorios: [] } : {}),
                      })} disabled={fijo('todo_el_dia')} data-testid="cal-editor-todo-dia" />
                      Todo el día
                    </label>
                    {!f.todo_el_dia && (
                      <label className="cal-inline">
                        <Globe className="h-3.5 w-3.5" style={{ color: 'var(--text-3)' }} />
                        <select className="cal-select cal-select--plano" value={f.zona} onChange={(e) => set({ zona: e.target.value })} disabled={fijo('zona')} aria-label="Zona horaria">
                          {zonas.map((z) => <option key={z} value={z}>{nombreZona(z)}</option>)}
                        </select>
                      </label>
                    )}
                  </div>
                  {!f.todo_el_dia && f.zona !== tz && (
                    <p className="cal-ayuda">
                      En tu zona: {capital(textoDiaLargo(ymdDe(deLocal(f.fIni, deHhmm(f.hIni), f.zona), tz)))}, {hora12(minutosDe(deLocal(f.fIni, deHhmm(f.hIni), f.zona), tz))}
                    </p>
                  )}
                </div>
              </div>

              {/* Repetición */}
              <div className="cal-campo">
                <Repeat className="cal-campo-ico" />
                <div className="flex-1 flex flex-col gap-2">
                  <select className="cal-select" value={f.repModo} onChange={(e) => set({ repModo: e.target.value })} disabled={fijo('rrule')} aria-label="Repetición" data-testid="cal-editor-repeticion">
                    {presets.map((p) => <option key={p.id} value={p.id}>{p.texto}</option>)}
                    <option value="custom">Personalizada…</option>
                  </select>
                  {f.repModo === 'custom' && (
                    <RepeticionPersonalizada valor={f.custom} fIni={f.fIni} onCambio={(custom) => set({ custom })} disabled={fijo('rrule')} />
                  )}
                </div>
              </div>

              {/* Orvian Meet */}
              {!fijo('ubicacion') && (
                <div className="cal-campo">
                  <Video className="cal-campo-ico" />
                  {meet ? (
                    <div className="cal-meet" data-testid="cal-editor-meet">
                      <a className="cal-meet-boton" href={meet.url} target="_blank" rel="noopener noreferrer">Unirse con Orvian Meet</a>
                      <span className="cal-meet-url">{meet.url.replace(/^https?:\/\//, '')}</span>
                      <button type="button" className="cal-mini-enlace" onClick={() => set({ ubicacion: f.ubicacion.replace(meet.url, '').trim() })} data-testid="cal-editor-meet-quitar">Quitar</button>
                    </div>
                  ) : (
                    <button type="button" className="cal-meet-anadir" onClick={anadirVideollamada} disabled={creandoMeet} data-testid="cal-editor-anadir-meet">
                      {creandoMeet ? 'Creando la videollamada…' : 'Añadir videollamada de Orvian Meet'}
                    </button>
                  )}
                </div>
              )}

              {/* Ubicación */}
              <div className="cal-campo">
                <MapPin className="cal-campo-ico" />
                <input className="cal-input flex-1" value={f.ubicacion} onChange={(e) => set({ ubicacion: e.target.value })} placeholder="Añade una ubicación o un enlace" aria-label="Ubicación" disabled={fijo('ubicacion')} data-testid="cal-editor-ubicacion" />
              </div>

              {/* Descripción */}
              <div className="cal-campo cal-campo--arriba">
                <AlignLeft className="cal-campo-ico" />
                <textarea className="cal-input flex-1" rows={4} value={f.descripcion} onChange={(e) => set({ descripcion: e.target.value })} placeholder="Añade una descripción" aria-label="Descripción" disabled={fijo('descripcion')} />
              </div>

              {/* Recordatorios */}
              <div className="cal-campo cal-campo--arriba">
                <Bell className="cal-campo-ico" />
                <div className="flex-1 flex flex-col gap-2">
                  {f.recordatorios.map((m, i) => {
                    const { valor, unidad } = partirRecordatorio(m);
                    const poner = (v, u) => {
                      const lista = [...f.recordatorios];
                      lista[i] = Math.max(0, Math.min(40320, Math.round(Number(v) || 0) * UNIDADES[u]));
                      set({ recordatorios: lista });
                    };
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-[13px]" style={{ color: 'var(--text-2)' }}>Notificación</span>
                        <input type="number" min="0" className="cal-input w-[72px]" value={valor} onChange={(e) => poner(e.target.value, unidad)} aria-label="Cantidad" />
                        <select className="cal-select" value={unidad} onChange={(e) => poner(valor, e.target.value)} aria-label="Unidad">
                          <option value="minutos">minutos</option>
                          <option value="horas">horas</option>
                          <option value="dias">días</option>
                          <option value="semanas">semanas</option>
                        </select>
                        <span className="text-[13px]" style={{ color: 'var(--text-2)' }}>antes</span>
                        <button type="button" className="cal-icono-btn" onClick={() => set({ recordatorios: f.recordatorios.filter((_, j) => j !== i) })} aria-label="Quitar notificación"><X className="h-4 w-4" /></button>
                      </div>
                    );
                  })}
                  {f.recordatorios.length < 5 && (
                    <button type="button" className="cal-mini-enlace self-start" onClick={() => set({ recordatorios: [...f.recordatorios, f.todo_el_dia ? 1440 : 30] })} data-testid="cal-editor-mas-recordatorio">
                      <Plus className="h-3.5 w-3.5" /> Añadir notificación
                    </button>
                  )}
                  <p className="cal-ayuda">Te avisamos en la campana, el navegador y el teléfono.</p>
                </div>
              </div>

              {/* Calendario, color, disponibilidad, privacidad */}
              <div className="cal-campo">
                <CalendarDays className="cal-campo-ico" />
                <select className="cal-select flex-1" value={f.calendario_id || ''} onChange={(e) => set({ calendario_id: e.target.value })} disabled={fijo('calendario_id') || calendarios.length < 2} aria-label="Calendario" data-testid="cal-editor-calendario">
                  {calendarios.map((c) => <option key={c.id} value={c.id}>{c.nombre}{c.dueno ? ` (${c.dueno.nombre})` : ''}</option>)}
                </select>
              </div>
              <div className="cal-campo cal-campo--arriba">
                <Palette className="cal-campo-ico" />
                <div className="cal-colores" role="radiogroup" aria-label="Color del evento">
                  <button type="button" className={`cal-color cal-color--defecto ${!f.color ? 'is-sel' : ''}`} onClick={() => set({ color: null })} title="Color del calendario" aria-label="Color del calendario" style={{ background: calendarios.find((c) => c.id === f.calendario_id)?.color }} />
                  {COLORES.map((c) => (
                    <button type="button" key={c.valor} className={`cal-color ${f.color === c.valor ? 'is-sel' : ''}`} style={{ background: c.valor }} onClick={() => set({ color: c.valor })} title={c.nombre} aria-label={c.nombre} />
                  ))}
                </div>
              </div>
              <div className="cal-campo cal-campo--arriba">
                <Briefcase className="cal-campo-ico" />
                <div className="flex-1 flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <select className="cal-select" value={f.disponibilidad} onChange={(e) => set({ disponibilidad: e.target.value })} aria-label="Mostrarme como" data-testid="cal-editor-disponibilidad">
                      <option value="ocupado">Mostrarme ocupado</option>
                      <option value="libre">Mostrarme libre</option>
                    </select>
                    <select className="cal-select" value={f.visibilidad} onChange={(e) => set({ visibilidad: e.target.value })} aria-label="Quién ve los detalles" data-testid="cal-editor-visibilidad">
                      <option value="normal">Detalles visibles</option>
                      <option value="privada">Privado</option>
                    </select>
                    <button type="button" className="cal-ayuda-btn" aria-expanded={verAyudaDisp} aria-label="Qué significa cada opción"
                      onClick={() => setVerAyudaDisp((v) => !v)} data-testid="cal-editor-ayuda-disponibilidad">?</button>
                  </div>
                  {verAyudaDisp && (
                    <div className="cal-ayuda-caja" role="note">
                      <p><b>Mostrarme ocupado:</b> a esa hora te ven sin hueco. Nadie te puede invitar sin ver el choque y tus páginas de reserva no ofrecen esa hora.</p>
                      <p><b>Mostrarme libre:</b> el evento queda en tu calendario como recordatorio, pero tu hora sigue disponible para invitaciones y reservas (útil para «trabajo desde casa» o cumpleaños).</p>
                      <p><b>Detalles visibles:</b> lo ve cada quien según el permiso que le diste a este calendario (solo libre/ocupado, ver detalles o hacer cambios).</p>
                      <p><b>Privado:</b> solo tú, quien puede editar el calendario y los invitados ven el título y los detalles; los demás solo ven un bloque «Ocupado».</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Invitados */}
            <div className="cal-editor-col cal-editor-col--invitados">
              <div className="cal-seccion-titulo"><Users className="h-4 w-4" /> Invitados</div>
              {!fijo('invitados') && (
                <CampoPersonas
                  onElegir={(p) => set({ invitados: [...f.invitados.filter((i) => i.correo !== p.correo), { correo: p.correo, nombre: p.nombre, opcional: false, estado: 'pendiente', interno: !!p.id }] })}
                  excluir={[...invitadoCorreos, user?.email || '']}
                  placeholder="Añadir invitados"
                  testId="cal-editor-invitados"
                />
              )}
              {f.invitados.length > 0 && (
                <ul className="cal-invitados cal-invitados--editor">
                  {f.invitados.map((i) => (
                    <li key={i.correo}>
                      <span className="cal-avatar">{initials(i.nombre, i.correo)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate" title={i.correo}>{i.nombre || i.correo}</span>
                        <span className="block cal-detalle-sub">
                          {i.estado && i.estado !== 'pendiente' ? `${{ aceptado: 'Asistirá', rechazado: 'No asistirá', quizas: 'Quizás' }[i.estado]}` : 'Pendiente'}
                          {i.opcional ? ' · Opcional' : ''}
                        </span>
                      </span>
                      {!fijo('invitados') && (
                        <>
                          <button type="button" className="cal-mini-enlace" onClick={() => set({ invitados: f.invitados.map((x) => (x.correo === i.correo ? { ...x, opcional: !x.opcional } : x)) })} title="Marcar como opcional">
                            {i.opcional ? 'Obligatorio' : 'Opcional'}
                          </button>
                          <button type="button" className="cal-icono-btn" onClick={() => set({ invitados: f.invitados.filter((x) => x.correo !== i.correo) })} aria-label={`Quitar a ${i.correo}`}><X className="h-4 w-4" /></button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {!fijo('inicio') && !f.todo_el_dia && (
                <button type="button" className="od-btn-secondary self-start" onClick={() => setBuscar((v) => !v)} data-testid="cal-editor-buscar-horario">
                  <Search className="h-4 w-4" /> {buscar ? 'Ocultar horarios' : 'Buscar horario'}
                </button>
              )}
              {buscar && !f.todo_el_dia && (
                <BuscarHorario
                  correos={[user?.email, ...invitadoCorreos].filter(Boolean)}
                  ymd={f.fIni}
                  zona={f.zona}
                  inicioMin={deHhmm(f.hIni)}
                  durMin={durMin}
                  onDia={(d) => cambiarInicio(d)}
                  onElegir={(min) => cambiarInicio(f.fIni, hhmm(min))}
                  yo={user?.email}
                />
              )}
              {f.invitados.some((i) => !i.interno) && (
                <p className="cal-ayuda">Los invitados de fuera de la organización recibirán un correo con la invitación desde tu buzón.</p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------- repetición personalizada */
function RepeticionPersonalizada({ valor, fIni, onCambio, disabled }) {
  const v = valor;
  const set = (c) => onCambio({ ...v, ...c });
  return (
    <div className="cal-subpanel" data-testid="cal-repeticion-personalizada">
      <div className="flex items-center gap-2 flex-wrap">
        <span>Repetir cada</span>
        <input type="number" min="1" max="99" className="cal-input w-[64px]" value={v.interval} onChange={(e) => set({ interval: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })} disabled={disabled} aria-label="Intervalo" />
        <select className="cal-select" value={v.freq} onChange={(e) => set({ freq: e.target.value, byday: e.target.value === 'WEEKLY' && !v.byday.length ? [BYDAY[diaSemana(fIni)]] : v.byday })} disabled={disabled} aria-label="Frecuencia">
          <option value="DAILY">{v.interval > 1 ? 'días' : 'día'}</option>
          <option value="WEEKLY">{v.interval > 1 ? 'semanas' : 'semana'}</option>
          <option value="MONTHLY">{v.interval > 1 ? 'meses' : 'mes'}</option>
          <option value="YEARLY">{v.interval > 1 ? 'años' : 'año'}</option>
        </select>
      </div>
      {v.freq === 'WEEKLY' && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="mr-1">Los</span>
          {BYDAY.map((d, i) => (
            <button
              type="button"
              key={d}
              className={`cal-dia-btn ${v.byday.includes(d) ? 'is-sel' : ''}`}
              onClick={() => set({ byday: v.byday.includes(d) ? v.byday.filter((x) => x !== d) : [...v.byday, d] })}
              disabled={disabled}
              aria-pressed={v.byday.includes(d)}
              aria-label={['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'][i]}
            >
              {DIAS_CORTOS[i]}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <span>Termina</span>
        <label className="cal-radio"><input type="radio" checked={v.fin === 'nunca'} onChange={() => set({ fin: 'nunca' })} disabled={disabled} /> Nunca</label>
        <label className="cal-radio">
          <input type="radio" checked={v.fin === 'fecha'} onChange={() => set({ fin: 'fecha', until: v.until || sumarDias(fIni, 90) })} disabled={disabled} /> El
          <input type="date" className="cal-input" value={v.until || ''} min={fIni} onChange={(e) => set({ fin: 'fecha', until: e.target.value })} disabled={disabled || v.fin !== 'fecha'} aria-label="Fecha de fin de la repetición" />
        </label>
        <label className="cal-radio">
          <input type="radio" checked={v.fin === 'veces'} onChange={() => set({ fin: 'veces' })} disabled={disabled} /> Después de
          <input type="number" min="1" max="999" className="cal-input w-[72px]" value={v.count} onChange={(e) => set({ fin: 'veces', count: Math.max(1, Math.min(999, Number(e.target.value) || 1)) })} disabled={disabled || v.fin !== 'veces'} aria-label="Número de repeticiones" />
          repeticiones
        </label>
      </div>
      <p className="cal-ayuda">{describirRrule(construirRrule(v))}</p>
    </div>
  );
}

/* ------------------------------------------------------- buscar horario */
const H0 = 6;
const H1 = 22;
const PX_H = 44;

function BuscarHorario({ correos, ymd, zona, inicioMin, durMin, onElegir, onDia, yo }) {
  const [datos, setDatos] = useState({ cargando: true, ocupado: {}, desconocidos: [] });
  const scroll = useRef(null);
  const clave = correos.join(',');
  // La franja propuesta siempre a la vista
  useEffect(() => {
    const el = scroll.current;
    if (el) el.scrollLeft = Math.max(0, ((inicioMin - H0 * 60) / 60) * PX_H - 40);
  }, [inicioMin, ymd]);
  useEffect(() => {
    let vivo = true;
    setDatos((d) => ({ ...d, cargando: true }));
    api.get('/calendar/disponibilidad', {
      params: { correos: clave, desde: isoUtc(deLocal(ymd, 0, zona)), hasta: isoUtc(deLocal(sumarDias(ymd, 1), 0, zona)) },
    }).then(({ data }) => {
      if (vivo) setDatos({ cargando: false, ocupado: data.ocupado || {}, desconocidos: data.desconocidos || [] });
    }).catch(() => { if (vivo) setDatos({ cargando: false, ocupado: {}, desconocidos: [] }); });
    return () => { vivo = false; };
  }, [clave, ymd, zona]);

  const ancho = (H1 - H0) * PX_H;
  const x = (min) => ((min - H0 * 60) / 60) * PX_H;
  const bloques = (lista) => (lista || []).map(([a, b]) => {
    const da = new Date(a); const db = new Date(b);
    const ma = ymdDe(da, zona) < ymd ? 0 : minutosDe(da, zona);
    const mb = ymdDe(db, zona) > ymd ? 1440 : minutosDe(db, zona);
    return [Math.max(ma, H0 * 60), Math.min(mb, H1 * 60)];
  }).filter(([a, b]) => b > a);

  return (
    <div className="cal-buscar" data-testid="cal-buscar-horario">
      <div className="cal-buscar-cab">
        <button type="button" className="cal-icono-btn" onClick={() => onDia(sumarDias(ymd, -1))} aria-label="Día anterior"><ChevronLeft className="h-4 w-4" /></button>
        <span className="flex-1 text-center font-medium">{capital(textoDia(ymd, { weekday: 'long', day: 'numeric', month: 'long' }))}</span>
        <button type="button" className="cal-icono-btn" onClick={() => onDia(sumarDias(ymd, 1))} aria-label="Día siguiente"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="cal-buscar-scroll od-scroll" ref={scroll}>
        <div className="cal-buscar-tabla" style={{ width: ancho + 96 }}>
          <div className="cal-buscar-fila cal-buscar-fila--horas">
            <span className="cal-buscar-nombre" />
            <div className="cal-buscar-pista" style={{ width: ancho }}>
              {Array.from({ length: H1 - H0 }, (_, i) => <span key={i} className="cal-buscar-hora" style={{ left: i * PX_H }}>{H0 + i}</span>)}
            </div>
          </div>
          {correos.map((c) => {
            const lista = datos.ocupado[c.toLowerCase()];
            const sinDatos = datos.desconocidos.includes(c.toLowerCase());
            return (
              <div key={c} className="cal-buscar-fila">
                <span className="cal-buscar-nombre" title={c}>{c === yo ? 'Tú' : c.split('@')[0]}</span>
                <div
                  className={`cal-buscar-pista ${sinDatos ? 'is-sin-datos' : ''}`}
                  style={{ width: ancho }}
                  onClick={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    const min = H0 * 60 + Math.floor(((e.clientX - r.left) / PX_H) * 2) * 30;
                    onElegir(Math.max(0, Math.min(1440 - 15, min)));
                  }}
                >
                  {Array.from({ length: H1 - H0 }, (_, i) => <span key={i} className="cal-buscar-linea" style={{ left: i * PX_H }} />)}
                  {bloques(lista).map(([a, b], i) => <span key={i} className="cal-buscar-ocupado" style={{ left: x(a), width: Math.max(3, x(b) - x(a)) }} />)}
                  <span className="cal-buscar-propuesta" style={{ left: x(inicioMin), width: Math.max(4, (durMin / 60) * PX_H) }} />
                  {sinDatos && <span className="cal-buscar-nota">Sin información de disponibilidad</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="cal-ayuda">{datos.cargando ? 'Consultando…' : 'Gris = ocupado. Pulsa en la franja para mover el evento a esa hora.'}</p>
    </div>
  );
}
